import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { prizeService } from '../services';
import BackButton from '../components/BackButton';
import { MovieTicketTextAmount } from '../components/MovieTicketIcon';
import { useFeedback } from '../components/FeedbackProvider';
import AdminPrizeEditorModal from './AdminPrizeEditorModal';
import {
  IMAGE_ACCEPT,
  PrizeBasicFields,
  PrizeOptionsSummary,
  PrizeStatusFields,
  buildPrizePayload,
  createEmptyOption,
  formFromPrizeItem,
  getEmptyPrizeForm
} from './PrizeAdminForm';
import './AdminPrizes.css';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_SOURCE_IMAGE_BYTES = 20 * 1024 * 1024;
const COMPRESS_TARGET_BYTES = 900 * 1024;
const COMPRESS_MAX_EDGE = 1600;
const COMPRESS_START_QUALITY = 0.86;
const COMPRESS_MIN_QUALITY = 0.72;
const COMPRESS_QUALITY_STEP = 0.07;
const COMPRESSIBLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
function getImageUrl(url) {
  if (!url) return 'https://via.placeholder.com/180x120/9b59b6/ffffff?text=Prize';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : '-';
}

function formatRedemptionCost(redemption) {
  const currencyType = redemption.currency_type || 'points';
  const cost = redemption.currency_cost ?? redemption.points_cost ?? 0;
  return currencyType === 'movie_ticket' ? <MovieTicketTextAmount amount={cost} /> : `${cost} 积分`;
}

function formatItemCosts(item) {
  if (item.price_ranges?.points || item.price_ranges?.movie_ticket) {
    const costs = [];
    if (item.price_ranges.points) {
      const range = item.price_ranges.points;
      costs.push(<span key="points">{range.min === range.max ? range.min : `${range.min}-${range.max}`} 积分</span>);
    }
    if (item.price_ranges.movie_ticket) {
      const range = item.price_ranges.movie_ticket;
      costs.push(<span key="movie_ticket">{range.min === range.max ? range.min : `${range.min}-${range.max}`} 电影票</span>);
    }
    return costs.map((cost, index) => (
      <React.Fragment key={cost.key || index}>
        {index > 0 && ' / '}
        {cost}
      </React.Fragment>
    ));
  }

  const costs = [];
  if (item.cost !== null && item.cost !== undefined && item.cost !== '') {
    costs.push(<span key="points">{item.cost} 积分</span>);
  }
  if (item.movie_ticket_cost !== null && item.movie_ticket_cost !== undefined && item.movie_ticket_cost !== '') {
    costs.push(<MovieTicketTextAmount key="movie_ticket" amount={item.movie_ticket_cost} />);
  }
  if (costs.length === 0) return '未设置价格';
  return costs.map((cost, index) => (
    <React.Fragment key={cost.key || index}>
      {index > 0 && ' / '}
      {cost}
    </React.Fragment>
  ));
}

function statusLabel(status) {
  return {
    pending: '待处理',
    completed: '已完成',
    cancelled: '已取消'
  }[status] || status;
}

function deliveryLabel(type) {
  return type === 'virtual' ? '虚拟商品' : '实体商品';
}

function readImageBlob(blob, filename) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ data_url: reader.result, filename });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function readImageFile(file) {
  return readImageBlob(file, file.name);
}

function isCompressibleImage(file) {
  const type = (file.type || '').toLowerCase();
  return COMPRESSIBLE_IMAGE_TYPES.has(type) || /\.(jpe?g|png|webp)$/i.test(file.name || '');
}

function getCompressedFilename(filename, mimeType = 'image/webp') {
  const safeName = filename || 'prize-image';
  const extension = mimeType === 'image/jpeg' ? '.jpg' : mimeType === 'image/png' ? '.png' : '.webp';
  return /\.(jpe?g|png|webp)$/i.test(safeName)
    ? safeName.replace(/\.[^.]+$/, extension)
    : `${safeName}${extension}`;
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} 图片读取失败`));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('图片压缩失败'));
      }
    }, mimeType, quality);
  });
}

async function compressImageFile(file) {
  const image = await loadImageElement(file);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const scale = Math.min(1, COMPRESS_MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new Error('浏览器不支持图片压缩');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let bestBlob = null;
  for (
    let quality = COMPRESS_START_QUALITY;
    quality >= COMPRESS_MIN_QUALITY - 0.001;
    quality -= COMPRESS_QUALITY_STEP
  ) {
    const blob = await canvasToBlob(canvas, 'image/webp', Number(quality.toFixed(2)));
    if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
    if (blob.size <= COMPRESS_TARGET_BYTES) break;
  }

  if (!bestBlob) throw new Error('图片压缩失败');
  return {
    blob: bestBlob,
    filename: getCompressedFilename(file.name, bestBlob.type)
  };
}

async function prepareImageFile(file) {
  if (!isCompressibleImage(file) || file.size <= COMPRESS_TARGET_BYTES) {
    return readImageFile(file);
  }

  try {
    const compressed = await compressImageFile(file);
    if (compressed.blob.size <= MAX_IMAGE_BYTES && compressed.blob.size < file.size) {
      return readImageBlob(compressed.blob, compressed.filename);
    }
  } catch (err) {
    if (file.size > MAX_IMAGE_BYTES) throw err;
  }

  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`${file.name} 压缩后仍超过 5MB，请换一张更小的图片`);
  }
  return readImageFile(file);
}

function validateImageFiles(files) {
  const notImage = files.find((file) => file.type && !file.type.startsWith('image/'));
  if (notImage) {
    throw new Error(`${notImage.name} 不是支持的图片文件`);
  }

  const oversizedSource = files.find((file) => file.size > MAX_SOURCE_IMAGE_BYTES);
  if (oversizedSource) {
    throw new Error(`${oversizedSource.name} 超过 20MB，请先压缩后再上传`);
  }

  const oversized = files.find((file) => !isCompressibleImage(file) && file.size > MAX_IMAGE_BYTES);
  if (oversized) {
    throw new Error(`${oversized.name} 超过 5MB，gif 等动图请先压缩后再上传`);
  }
}

function getUploadErrorMessage(err) {
  if (err.response?.status === 413) {
    return '图片请求被服务器拦截了。请确认 HTTPS 的 Nginx server 配置了 client_max_body_size 50m，并重载 Nginx';
  }
  return err.response?.data?.message || err.message;
}

function AdminPrizes() {
  const { confirm, prompt, success: showSuccess, error: showError } = useFeedback();
  const navigate = useNavigate();
  const location = useLocation();
  const { prizeId } = useParams();
  const routeWantsEditor = location.pathname.endsWith('/new') || Boolean(prizeId);
  const [items, setItems] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState(getEmptyPrizeForm);
  const [editorState, setEditorState] = useState(null);
  const [activeTab, setActiveTab] = useState('items');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingItemId, setTogglingItemId] = useState(null);
  const [pendingCreateImages, setPendingCreateImages] = useState([]);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const itemsRef = useRef([]);
  const dragSnapshotRef = useRef(null);
  const dropHandledRef = useRef(false);
  const [redemptionFilters, setRedemptionFilters] = useState({
    status: 'pending',
    search: '',
    prize_id: ''
  });

  const loadItems = useCallback(async () => {
    const response = await prizeService.getAdminItems();
    const nextItems = response.data || [];
    setItems(nextItems);
    return nextItems;
  }, []);

  const loadRedemptions = useCallback(async () => {
    const response = await prizeService.getAdminRedemptions(redemptionFilters);
    setRedemptions(response.data || []);
  }, [redemptionFilters]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await loadItems();
      } catch (err) {
        setError(err.response?.data?.message || '加载兑换后台失败');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadItems]);

  const totals = useMemo(() => {
    return items.reduce((acc, item) => {
      acc.items += 1;
      acc.active += item.is_active ? 1 : 0;
      acc.stock += Number(item.available_stock ?? item.stock ?? 0);
      acc.pending += Number(item.stats?.pending_redemptions || 0);
      return acc;
    }, { items: 0, active: 0, stock: 0, pending: 0 });
  }, [items]);

  const selectItem = (item) => {
    setSelectedItem(item);
    setForm(formFromPrizeItem(item));
    setPendingCreateImages([]);
    setActiveTab('items');
    setMessage('');
    setError('');
  };

  const startCreate = () => {
    setSelectedItem(null);
    setForm(getEmptyPrizeForm());
    setPendingCreateImages([]);
    setActiveTab('items');
    setMessage('');
    setError('');
  };

  const refreshSelected = async (nextItem) => {
    const nextItems = await loadItems();
    if (nextItem) {
      const refreshed = nextItems.find((item) => String(item.id) === String(nextItem.id)) || nextItem;
      setSelectedItem(refreshed);
      setForm(formFromPrizeItem(refreshed));
    }
  };

  const uploadPrizeFiles = async (prizeId, files) => {
    let latestResponse = null;

    for (const file of files) {
      const image = file.data_url ? file : await prepareImageFile(file);
      latestResponse = await prizeService.uploadPrizeImages(prizeId, [image]);
    }

    return latestResponse;
  };

  const openFullEditor = (draft = form) => {
    setEditorState({
      prizeId: selectedItem ? selectedItem.id : null,
      draft
    });
  };

  const openFullEditorWithNewOption = () => {
    const draft = {
      ...form,
      options: [...(form.options || []), createEmptyOption()]
    };
    openFullEditor(draft);
  };

  const openEditorForItem = (item, event) => {
    event?.stopPropagation();
    setEditorState({ prizeId: item.id, draft: null });
    setMessage('');
    setError('');
  };

  const closeEditor = () => {
    setEditorState(null);
    if (routeWantsEditor) {
      navigate('/admin/prizes', { replace: true, state: null });
    }
  };

  const handleEditorSaved = async (savedItem, meta = {}) => {
    const nextItems = await loadItems();
    const refreshed = nextItems.find((item) => String(item.id) === String(savedItem.id)) || savedItem;
    setSelectedItem(refreshed);
    setForm(formFromPrizeItem(refreshed));
    setPendingCreateImages([]);
    setMessage(meta.wasNew ? '礼物已创建' : '礼物已更新');

    if (meta.wasNew) {
      if (routeWantsEditor) {
        navigate(`/admin/prizes/${refreshed.id}/edit`, { replace: true, state: null });
      } else {
        setEditorState({ prizeId: refreshed.id, draft: null });
      }
    }
  };

  const handleEditorDeleted = async (deletedItem) => {
    await loadItems();
    if (selectedItem?.id === deletedItem.id) {
      startCreate();
    }
    closeEditor();
    setMessage('礼物已删除');
  };

  const handleToggleItemActive = async (item, event) => {
    event?.stopPropagation();
    setTogglingItemId(item.id);
    setError('');
    setMessage('');

    try {
      const payload = buildPrizePayload({
        ...formFromPrizeItem(item),
        is_active: !item.is_active
      });
      const response = await prizeService.updateAdminItem(item.id, payload);
      const nextItems = await loadItems();
      const updatedItem = nextItems.find((entry) => String(entry.id) === String(item.id)) || response.data;

      if (selectedItem?.id === item.id) {
        setSelectedItem(updatedItem);
        setForm(formFromPrizeItem(updatedItem));
      }

      setMessage(updatedItem.is_active ? '礼物已上架' : '礼物已下架');
    } catch (err) {
      setError(err.response?.data?.message || '更新上下架状态失败');
    } finally {
      setTogglingItemId(null);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const payload = buildPrizePayload(form);
      const response = selectedItem
        ? await prizeService.updateAdminItem(selectedItem.id, payload)
        : await prizeService.createAdminItem(payload);

      let savedItem = response.data;
      if (!selectedItem && pendingCreateImages.length > 0) {
        const uploadResponse = await uploadPrizeFiles(savedItem.id, pendingCreateImages);
        savedItem = uploadResponse?.data || savedItem;
      }

      setPendingCreateImages([]);
      setMessage(selectedItem ? '礼物已更新' : '礼物已创建');
      await refreshSelected(savedItem);
    } catch (err) {
      setError(getUploadErrorMessage(err) || '保存礼物失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemToDelete = selectedItem) => {
    if (!itemToDelete) return;
    const confirmed = await confirm({
      title: '删除礼物',
      message: `确定删除「${itemToDelete.name}」吗？`,
      detail: '删除后用户兑换页和后台礼物列表都不会再显示它。',
      confirmText: '删除礼物',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      setError('');
      setMessage('');
      await prizeService.deleteAdminItem(itemToDelete.id);
      if (selectedItem?.id === itemToDelete.id) {
        setSelectedItem(null);
        setForm(getEmptyPrizeForm());
        setPendingCreateImages([]);
      }
      setMessage('礼物已删除');
      await loadItems();
    } catch (err) {
      setError(err.response?.data?.message || '删除礼物失败');
    }
  };

  const moveItemInList = (list, fromIndex, toIndex) => {
    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  };

  const handleItemDragStart = (event, itemId) => {
    setDraggingItemId(itemId);
    dragSnapshotRef.current = items;
    dropHandledRef.current = false;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(itemId));
  };

  const handleItemDragOver = (event) => {
    event.preventDefault();
  };

  const handleItemDragEnter = (targetItemId) => {
    if (!draggingItemId || draggingItemId === targetItemId) return;
    if (dragOverItemId === targetItemId) return;

    setDragOverItemId(targetItemId);
    setItems((currentItems) => {
      const fromIndex = currentItems.findIndex((item) => item.id === draggingItemId);
      const toIndex = currentItems.findIndex((item) => item.id === targetItemId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return currentItems;
      const nextItems = moveItemInList(currentItems, fromIndex, toIndex);
      itemsRef.current = nextItems;
      return nextItems;
    });
  };

  const handleItemDrop = async (event) => {
    event.preventDefault();
    dropHandledRef.current = true;
    const orderedIds = itemsRef.current.map((item) => item.id);
    setDraggingItemId(null);
    setDragOverItemId(null);
    setSavingOrder(true);
    setError('');
    setMessage('');

    try {
      const response = await prizeService.updateAdminItemOrder(orderedIds);
      const nextItems = response.data || [];
      setItems(nextItems);
      itemsRef.current = nextItems;
      if (selectedItem) {
        const nextSelected = nextItems.find((item) => item.id === selectedItem.id);
        if (nextSelected) selectItem(nextSelected);
      }
      setMessage('礼物排序已保存');
    } catch (err) {
      setError(err.response?.data?.message || '保存礼物排序失败');
      await loadItems();
    } finally {
      setSavingOrder(false);
      dragSnapshotRef.current = null;
    }
  };

  const handleItemDragEnd = () => {
    if (!dropHandledRef.current && dragSnapshotRef.current) {
      setItems(dragSnapshotRef.current);
      itemsRef.current = dragSnapshotRef.current;
    }
    setDraggingItemId(null);
    setDragOverItemId(null);
    dragSnapshotRef.current = null;
    dropHandledRef.current = false;
  };

  const handleCreateImageSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      validateImageFiles(files);
      const images = await Promise.all(files.map((file) => prepareImageFile(file)));
      setPendingCreateImages((current) => [...current, ...images]);
    } catch (err) {
      setError(getUploadErrorMessage(err) || '读取图片失败');
    } finally {
      event.target.value = '';
    }
  };

  const removePendingCreateImage = (index) => {
    setPendingCreateImages((current) => current.filter((_, imageIndex) => imageIndex !== index));
  };

  const updateRedemptionStatus = async (redemption, status) => {
    const action = status === 'completed' ? '标记完成' : '取消并退款';
    let reason = '';
    if (status === 'cancelled') {
      const input = await prompt({
        title: '取消兑换并退款',
        message: `确定要取消「${redemption.prize_name}」这条兑换并原路退回货币和库存吗？`,
        label: '取消原因（可选）',
        placeholder: '例如：用户申请取消、库存异常等',
        confirmText: '确认取消并退款',
        multiline: true
      });
      if (input === null) return;
      reason = input;
    } else {
      const confirmed = await confirm({
        title: '完成兑换',
        message: `确定要${action}这条兑换吗？`,
        confirmText: '标记完成'
      });
      if (!confirmed) return;
    }

    try {
      setError('');
      setMessage('');
      await prizeService.updateRedemptionStatus(redemption.id, status, reason);
      setMessage(status === 'completed' ? '兑换已完成' : '兑换已取消，货币和库存已退回');
      await loadItems();
      await loadRedemptions();
    } catch (err) {
      setError(err.response?.data?.message || '更新兑换状态失败');
    }
  };

  const copyRedemptionAddress = async (redemption) => {
    const addressText = [
      redemption.recipient_name_snapshot,
      redemption.phone_snapshot,
      `${redemption.region_snapshot || ''}${redemption.detail_address_snapshot || ''}`
    ].filter(Boolean).join(' ');
    try {
      await navigator.clipboard.writeText(addressText);
      showSuccess('收货地址已复制');
    } catch (err) {
      showError('复制失败，请手动选择地址文本');
    }
  };

  const activeEditor = editorState || (routeWantsEditor ? {
    prizeId: prizeId || null,
    draft: location.state?.draft || null
  } : null);

  if (loading) {
    return <div className="loading">正在加载礼物后台...</div>;
  }

  return (
    <div className="admin-prizes">
      <BackButton to="/" />
      <header className="admin-prizes-header">
        <div>
          <h1>兑换礼物管理</h1>
          <p>维护礼物、多图展示、价格、库存和子选项；订单处理已迁移到独立页面。</p>
        </div>
        <div className="admin-prizes-tabs">
          <button className={activeTab === 'items' ? 'active' : ''} onClick={() => setActiveTab('items')}>礼物</button>
          <button type="button" onClick={() => navigate('/admin/prize-orders')}>订单管理</button>
        </div>
      </header>

      {message && <div className="prizes-success">{message}</div>}
      {error && <div className="prizes-error">{error}</div>}

      <section className="prizes-metrics">
        <div><span>礼物数</span><strong>{totals.items}</strong></div>
        <div><span>上架中</span><strong>{totals.active}</strong></div>
        <div><span>总库存</span><strong>{totals.stock}</strong></div>
        <div><span>待处理兑换</span><strong>{totals.pending}</strong></div>
      </section>

      {activeTab === 'items' ? (
        <main className="admin-prizes-layout">
          <section className="prize-items-panel">
            <div className="panel-toolbar">
              <span className="panel-title-hint">拖动排序</span>
              <h2>礼物列表</h2>
              <button className="secondary-action" onClick={startCreate}>新建礼物</button>
            </div>
            <div className="prize-admin-list">
              {items.map((item) => (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  draggable={!savingOrder}
                  className={`prize-admin-row ${selectedItem?.id === item.id ? 'selected' : ''} ${draggingItemId === item.id ? 'dragging' : ''}`}
                  onDragStart={(event) => handleItemDragStart(event, item.id)}
                  onDragOver={handleItemDragOver}
                  onDragEnter={() => handleItemDragEnter(item.id)}
                  onDrop={handleItemDrop}
                  onDragEnd={handleItemDragEnd}
                  onClick={() => selectItem(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectItem(item);
                    }
                  }}
                >
                  <span className="drag-handle" aria-hidden="true">⋮⋮</span>
                  <img src={getImageUrl(item.image_url)} alt={item.name} />
                  <div className="prize-row-main">
                    <div className="prize-row-title-line">
                      <span className={`status-pill ${item.is_active ? 'active' : 'inactive'}`}>
                        {item.is_active ? '上架' : '下架'}
                      </span>
                      <strong>{item.name}</strong>
                    </div>
                    <span>
                      <em className={`delivery-badge ${item.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>{deliveryLabel(item.delivery_type)}</em>
                      {formatItemCosts(item)} · 库存 {item.available_stock ?? item.stock}
                      {(item.options || []).length > 0 && ` · ${item.options.length} 个选项`}
                    </span>
                  </div>
                  <div
                    className="prize-row-actions"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <button type="button" className="secondary-action compact-action" onClick={(event) => openEditorForItem(item, event)}>
                      编辑
                    </button>
                    <button
                      type="button"
                      className={item.is_active ? 'danger-action compact-action' : 'primary-action compact-action'}
                      disabled={togglingItemId === item.id}
                      onClick={(event) => handleToggleItemActive(item, event)}
                    >
                      {togglingItemId === item.id ? '处理中...' : item.is_active ? '下架' : '上架'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="prize-editor-panel prize-side-panel">
            {selectedItem ? (
              <div className="prize-preview-panel">
                <div className="panel-toolbar">
                  <div>
                    <h2>礼物预览</h2>
                    <p>点击编辑进入完整配置弹窗。</p>
                  </div>
                  <button type="button" className="secondary-action" onClick={startCreate}>
                    新建礼物
                  </button>
                </div>

                <div className="prize-preview-card">
                  <img src={getImageUrl(selectedItem.image_url)} alt={selectedItem.name} />
                  <div className="prize-preview-main">
                    <div className="prize-preview-title">
                      <span className={`status-pill ${selectedItem.is_active ? 'active' : 'inactive'}`}>
                        {selectedItem.is_active ? '上架' : '下架'}
                      </span>
                      <h3>{selectedItem.name}</h3>
                    </div>
                    <span className={`delivery-badge ${selectedItem.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>
                      {deliveryLabel(selectedItem.delivery_type)}
                    </span>
                    <p>{selectedItem.description || '暂无描述'}</p>
                  </div>
                </div>

                <div className="prize-preview-stats">
                  <div><span>价格</span><strong>{formatItemCosts(selectedItem)}</strong></div>
                  <div><span>库存</span><strong>{selectedItem.available_stock ?? selectedItem.stock}</strong></div>
                  <div><span>子选项</span><strong>{(selectedItem.options || []).length}</strong></div>
                  <div><span>图片</span><strong>{(selectedItem.images || []).length}</strong></div>
                </div>

                {(selectedItem.options || []).length > 0 && (
                  <div className="prize-preview-options">
                    {(selectedItem.options || []).slice(0, 6).map((option) => (
                      <span key={option.id || option.name}>{option.name || '未命名选项'} · 库存 {option.stock || 0}</span>
                    ))}
                    {(selectedItem.options || []).length > 6 && <span>+{selectedItem.options.length - 6} 个选项</span>}
                  </div>
                )}

                <div className="prize-preview-actions">
                  <button type="button" className="primary-action" onClick={(event) => openEditorForItem(selectedItem, event)}>
                    编辑
                  </button>
                  <button
                    type="button"
                    className={selectedItem.is_active ? 'danger-action' : 'primary-action'}
                    disabled={togglingItemId === selectedItem.id}
                    onClick={(event) => handleToggleItemActive(selectedItem, event)}
                  >
                    {togglingItemId === selectedItem.id ? '处理中...' : selectedItem.is_active ? '下架' : '上架'}
                  </button>
                  <button type="button" className="danger-action" onClick={() => handleDeleteItem(selectedItem)}>
                    删除
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="panel-toolbar">
                  <div>
                    <h2>新建礼物</h2>
                    <p>适合快速创建简单商品；复杂规格请进入完整新建。</p>
                  </div>
                  <button type="button" className="secondary-action" onClick={() => openFullEditor()}>
                    完整新建
                  </button>
                </div>
                <form onSubmit={handleSave} className="prize-editor-form">
                  <PrizeBasicFields form={form} setForm={setForm} />
                  <PrizeOptionsSummary form={form} onOpenFullEditor={() => openFullEditor()} onAddOption={openFullEditorWithNewOption} />
                  <PrizeStatusFields form={form} setForm={setForm} />

                  <label className="file-button wide-file-button">
                    选择礼物图片
                    <input type="file" accept={IMAGE_ACCEPT} multiple onChange={handleCreateImageSelect} />
                  </label>
                  {pendingCreateImages.length > 0 && (
                    <div className="pending-image-list">
                      {pendingCreateImages.map((image, index) => (
                        <span key={`${image.filename || 'pending'}-${index}`}>
                          {image.filename || `图片 ${index + 1}`}
                          <button type="button" onClick={() => removePendingCreateImage(index)}>移除</button>
                        </span>
                      ))}
                    </div>
                  )}

                  <button className="primary-action" disabled={saving}>
                    {saving ? '保存中...' : '保存礼物'}
                  </button>
                </form>
              </>
            )}
          </section>
        </main>
      ) : (
        <section className="redemptions-panel">
          <form
            className="redemption-filters"
            onSubmit={(event) => {
              event.preventDefault();
              loadRedemptions();
            }}
          >
            <input
              placeholder="搜索用户名、邮箱、B站UID、昵称或礼物"
              value={redemptionFilters.search}
              onChange={(event) => setRedemptionFilters({ ...redemptionFilters, search: event.target.value })}
            />
            <select
              value={redemptionFilters.status}
              onChange={(event) => setRedemptionFilters({ ...redemptionFilters, status: event.target.value })}
            >
              <option value="all">全部状态</option>
              <option value="pending">待处理</option>
              <option value="completed">已完成</option>
              <option value="cancelled">已取消</option>
            </select>
            <select
              value={redemptionFilters.prize_id}
              onChange={(event) => setRedemptionFilters({ ...redemptionFilters, prize_id: event.target.value })}
            >
              <option value="">全部礼物</option>
              {items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <button className="primary-action">筛选</button>
          </form>

          <div className="redemption-table-wrap">
            <table className="redemption-table">
              <thead>
                <tr>
                  <th>用户</th>
                  <th>B站账户</th>
                  <th>礼物</th>
                  <th>价格</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>处理</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((redemption) => (
                  <tr key={redemption.id}>
                    <td>
                      <strong>{redemption.username}</strong>
                      <span className="redemption-subtext">{redemption.email}</span>
                    </td>
                    <td>
                      <strong>{redemption.bilibili_uname || '-'}</strong>
                      <span className="redemption-subtext">{redemption.bilibili_uid || '-'}</span>
                    </td>
                    <td>
                      <strong>{redemption.prize_name}</strong>
                      <span className={`redemption-subtext delivery-text ${redemption.delivery_type === 'virtual' ? 'virtual' : 'physical'}`}>
                        {deliveryLabel(redemption.delivery_type)}
                      </span>
                      {(redemption.option_name || Number(redemption.quantity || 1) > 1) && (
                        <span className="redemption-subtext">
                          {redemption.option_name || '默认'} · x{redemption.quantity || 1}
                        </span>
                      )}
                      {redemption.user_remark && (
                        <span className="redemption-subtext">备注：{redemption.user_remark}</span>
                      )}
                      {redemption.delivery_type !== 'virtual' && redemption.recipient_name_snapshot && (
                        <span className="redemption-subtext">
                          {redemption.recipient_name_snapshot} · {redemption.phone_snapshot} · {redemption.region_snapshot}{redemption.detail_address_snapshot}
                          <button type="button" className="inline-copy-button" onClick={() => copyRedemptionAddress(redemption)}>复制地址</button>
                        </span>
                      )}
                    </td>
                    <td>{formatRedemptionCost(redemption)}</td>
                    <td><span className={`status-pill ${redemption.status}`}>{statusLabel(redemption.status)}</span></td>
                    <td>{formatDate(redemption.created_at)}</td>
                    <td>
                      {redemption.status === 'pending' ? (
                        <div className="row-actions">
                          <button onClick={() => updateRedemptionStatus(redemption, 'completed')}>完成</button>
                          <button className="danger-text" onClick={() => updateRedemptionStatus(redemption, 'cancelled')}>取消</button>
                        </div>
                      ) : (
                        <span className="handled-text">{redemption.handled_by_username || '已处理'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {redemptions.length === 0 && <div className="empty-inline">没有符合条件的兑换记录</div>}
          </div>
        </section>
      )}
      {activeEditor && (
        <AdminPrizeEditorModal
          prizeId={activeEditor.prizeId}
          draft={activeEditor.draft}
          items={items}
          onClose={closeEditor}
          onSaved={handleEditorSaved}
          onDeleted={handleEditorDeleted}
        />
      )}
    </div>
  );
}

export default AdminPrizes;
